import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvent } from '../context/EventContext';
import Button from '../components/Button';
import TaskDetailModal from '../components/TaskDetailModal';
import NotificationModal from '../components/NotificationModal';

const VendorDetail = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { eventData, refreshTasks } = useEvent();
  const [vendor, setVendor] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [currentUser, setCurrentUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    const loadVendorData = async () => {
      if (!eventData?.eventId || !vendorId) return;
      
      setLoading(true);
      try {
        // Get vendor details
        const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}`);
        if (vendorResponse.ok) {
          const vendorData = await vendorResponse.json();
          const standardized = {};
          for (const [key, value] of Object.entries(vendorData)) {
            if (value?.S !== undefined) standardized[key] = value.S;
            else if (value?.N !== undefined) standardized[key] = parseInt(value.N);
            else if (value?.BOOL !== undefined) standardized[key] = value.BOOL;
            else standardized[key] = value;
          }
          setVendor(standardized);
        }

        // Get tasks for this vendor
        const tasksResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/tasks`);
        if (tasksResponse.ok) {
          const allTasks = await tasksResponse.json();
          const standardizedTasks = allTasks.map(task => {
            const standardized = {};
            for (const [key, value] of Object.entries(task)) {
              if (value?.S !== undefined) standardized[key] = value.S;
              else if (value?.N !== undefined) standardized[key] = parseInt(value.N);
              else if (value?.BOOL !== undefined) standardized[key] = value.BOOL;
              else standardized[key] = value;
            }
            return standardized;
          });
          const vendorTasks = standardizedTasks.filter(task => task.vendorId === vendorId);
          setTasks(vendorTasks);
        }

        // Get quotes and invoices for this vendor
        const invoicesResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/invoices?eventId=${eventData.eventId}`);
        if (invoicesResponse.ok) {
          const allInvoices = await invoicesResponse.json();
          const vendorQuotes = allInvoices.filter(inv => inv.vendorId === vendorId && inv.invoiceType === 'quote');
          const vendorInvoices = allInvoices.filter(inv => inv.vendorId === vendorId && inv.invoiceType === 'invoice');
          setQuotes(vendorQuotes);
          setInvoices(vendorInvoices);
        }
      } catch (error) {
        console.error('Error loading vendor data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVendorData();
  }, [eventData?.eventId, vendorId]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading vendor...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        <h3>Vendor not found</h3>
        <button onClick={() => navigate('/vendors')} style={{ marginTop: '20px', padding: '12px 24px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Back to Vendors
        </button>
      </div>
    );
  }

  const upcomingTasks = tasks.filter(t => t.status !== 'completed').length;

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: '#E8F4E8',
        padding: '48px 24px 200px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '60px'
      }}>
        <h1 style={{ 
          fontSize: '56px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          {vendor.name}
        </h1>
        
        {/* Stats Card */}
        <div style={{ 
          position: 'absolute',
          bottom: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {upcomingTasks} <span style={{ fontSize: '20px', color: '#000000', fontWeight: 'normal' }}>upcoming items</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {/* Contact Details */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          marginBottom: '32px'
        }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '400', 
            marginBottom: '20px',
            color: '#1F2937',
            fontFamily: 'Georgia, serif'
          }}>
            Contact Details
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {vendor.email && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Email</div>
                <a href={`mailto:${vendor.email}`} style={{ fontSize: '16px', color: '#6B9080', textDecoration: 'none' }}>
                  {vendor.email}
                </a>
              </div>
            )}
            {vendor.phone && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Phone</div>
                <a href={`tel:${vendor.phone}`} style={{ fontSize: '16px', color: '#6B9080', textDecoration: 'none' }}>
                  {vendor.phone}
                </a>
              </div>
            )}
            {vendor.website && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Website</div>
                <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '16px', color: '#6B9080', textDecoration: 'none' }}>
                  {vendor.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Quotes & Invoices */}
        {(quotes.length > 0 || invoices.length > 0) && (
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '32px', 
              fontWeight: '400', 
              marginBottom: '24px',
              color: '#1F2937',
              fontFamily: 'Georgia, serif'
            }}>
              Quotes & Invoices
            </h2>

            {/* Quotes */}
            {quotes.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '16px', color: '#1F2937' }}>
                  Quotes ({quotes.length})
                </h3>
                {quotes.map(quote => {
                  const getQuoteStatusBadge = () => {
                    if (quote.status === 'accepted') return { text: 'ACCEPTED', bg: '#D1FAE5', color: '#065F46' };
                    else if (quote.status === 'rejected') return { text: 'REJECTED', bg: '#FEE2E2', color: '#991B1B' };
                    else return { text: 'PENDING', bg: '#FEF3C7', color: '#92400E' };
                  };
                  const statusBadge = getQuoteStatusBadge();
                  
                  return (
                    <div 
                      key={quote.id} 
                      style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        setSelectedQuote(quote);
                        setShowQuoteDetailsModal(true);
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937' }}>
                            {quote.name || `Quote #${quote.id?.split('-')[1] || 'Unknown'}`}
                          </div>
                          <span style={{ padding: '4px 12px', backgroundColor: statusBadge.bg, color: statusBadge.color, borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                            {statusBadge.text}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {(() => {
                            if (!quote.dueDate) return 'No due date';
                            try {
                              const date = new Date(quote.dueDate);
                              if (isNaN(date.getTime())) return 'Invalid date';
                              return `Due ${date.toLocaleDateString()}`;
                            } catch {
                              return 'Invalid date';
                            }
                          })()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937' }}>
                          ${quote.depositAmount || 0}
                        </div>
                        {quote.pdfUrl && (
                          <Button 
                            variant="outline" 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(quote.pdfUrl, '_blank');
                            }}>
                            View Document
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Invoices */}
            {invoices.length > 0 && (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '16px', color: '#1F2937' }}>
                  Invoices ({invoices.length})
                </h3>
                {invoices.map(invoice => {
                  const getInvoiceStatusBadge = () => {
                    if (invoice.status === 'paid') return { text: 'PAID', bg: '#D1FAE5', color: '#065F46' };
                    else if (invoice.status === 'overdue') return { text: 'OVERDUE', bg: '#FEE2E2', color: '#991B1B' };
                    else return { text: 'PENDING', bg: '#FEF3C7', color: '#92400E' };
                  };
                  const statusBadge = getInvoiceStatusBadge();
                  
                  return (
                    <div 
                      key={invoice.id} 
                      style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        setSelectedQuote(invoice);
                        setShowQuoteDetailsModal(true);
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937' }}>
                            {invoice.name || `Invoice #${invoice.id?.split('-')[1] || 'Unknown'}`}
                          </div>
                          <span style={{ padding: '4px 12px', backgroundColor: statusBadge.bg, color: statusBadge.color, borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                            {statusBadge.text}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {(() => {
                            if (!invoice.dueDate) return 'No due date';
                            try {
                              const date = new Date(invoice.dueDate);
                              if (isNaN(date.getTime())) return 'Invalid date';
                              return `Due ${date.toLocaleDateString()}`;
                            } catch {
                              return 'Invalid date';
                            }
                          })()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937' }}>
                          ${invoice.depositAmount || invoice.total || 0}
                        </div>
                        {invoice.pdfUrl && (
                          <Button 
                            variant="outline" 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(invoice.pdfUrl, '_blank');
                            }}>
                            View Document
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Checklist Items */}
        <h2 style={{ 
          fontSize: '32px', 
          fontWeight: '400', 
          marginBottom: '24px',
          color: '#1F2937',
          fontFamily: 'Georgia, serif'
        }}>
          Checklist Items
        </h2>

        {tasks.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            textAlign: 'center',
            color: '#6B7280'
          }}>
            No checklist items yet
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            {tasks.map((task) => (
              <div 
                key={task.id}
                style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => {
                  setSelectedTask(task);
                  setShowDetailView(true);
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#E8F4E8',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: '#6B9080'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '8px' }}>
                    {task.name}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {task.documentUrl && (
                      <Button 
                        variant="outline" 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(task.documentUrl, '_blank');
                        }}
                      >
                        Open Document
                      </Button>
                    )}
                  </div>
                </div>
                <div style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: task.status === 'completed' ? 'white' : '#6B7280',
                  fontSize: '16px'
                }}>
                  {task.status === 'completed' ? '✓' : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button 
          variant="primary" 
          size="large"
          onClick={() => navigate('/vendors')}
        >
          Back to Vendors
        </Button>
      </div>

      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => {
          setShowDetailView(false);
          setSelectedTask(null);
        }}
        eventId={eventData?.eventId}
        currentUser={currentUser}
        onStatusUpdate={refreshTasks}
      />

      <NotificationModal
        isOpen={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />
      
      {/* Quote/Invoice Details Modal */}
      {showQuoteDetailsModal && selectedQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '500',
              color: '#1F2937',
              marginBottom: '20px',
              fontFamily: 'Georgia, serif'
            }}>
              {selectedQuote.invoiceType === 'quote' ? 'Quote' : 'Invoice'} Details
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Name</div>
              <div style={{ fontSize: '16px', color: '#1F2937' }}>{selectedQuote.name || 'Unnamed'}</div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Amount</div>
              <div style={{ fontSize: '16px', color: '#1F2937' }}>${selectedQuote.depositAmount || selectedQuote.total || 0}</div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Due Date</div>
              <div style={{ fontSize: '16px', color: '#1F2937' }}>
                {selectedQuote.dueDate ? new Date(selectedQuote.dueDate).toLocaleDateString() : 'No due date'}
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Status</div>
              <div style={{ fontSize: '16px', color: '#1F2937', textTransform: 'capitalize' }}>{selectedQuote.status || 'Pending'}</div>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Created</div>
              <div style={{ fontSize: '16px', color: '#1F2937' }}>
                {selectedQuote.createdAt ? new Date(selectedQuote.createdAt).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {selectedQuote.pdfUrl && (
                <button
                  onClick={() => window.open(selectedQuote.pdfUrl, '_blank')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  View Document
                </button>
              )}
              <button
                onClick={() => setShowQuoteDetailsModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDetail;
